/**
 * Entidade Event - Eventos online, presenciais e híbridos
 * Suporte a gratuitos e pagos, múltiplos tipos de ingressos
 */

export type EventStatus = 'rascunho' | 'publicado' | 'cancelado' | 'finalizado';
export type EventVisibility = 'publico' | 'privado';
export type EventFormat = 'online' | 'presencial' | 'hibrido';
export type EventLayoutType = 'normal' | 'matéria_principal' | 'slide_principal';
export type EventPeriodoStatusTipo = 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria';

export interface TicketType {
  id: string;
  name: string;
  price: number;
  quantity: number;
  /** Moeda do tipo de ingresso (BRL ou USD) */
  currency?: string;
}

export interface Event {
  id: string;
  /** ID do estabelecimento associado (opcional) */
  estabelecimentoId?: string;
  /** STEP 1 */
  title: string;
  shortDescription: string;
  description: string;
  coverImage: string;
  categoryId: string;
  tags: string[];
  /** STEP 2 */
  startDate: string; // YYYY-MM-DD
  endDate: string;
  startTime: string; // HH:mm
  endTime: string;
  timezone: string;
  format?: EventFormat;
  /** STEP 3/4 */
  isFree: boolean;
  maxParticipants?: number;
  ticketTypes: TicketType[];
  /** STEP 5 */
  visibility: EventVisibility;
  status: EventStatus;
  /** Visibilidade no calendário (fora de períodos programados). */
  calendarioStatusPadrao?: 'ativado' | 'desativado';
  periodoAgendaDe?: string;
  periodoAgendaAte?: string;
  periodosStatus?: { de: string; ate: string; tipo: EventPeriodoStatusTipo }[];
  layoutType?: EventLayoutType;
  layoutExpiresAt?: string;
  destaqueNaviex?: boolean;
  destaqueNaviexExpiresAt?: string;
  destaqueSubcategoria?: boolean;
  destaqueSubcategoriaExpiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

const KEY_EVENTS = 'naviex-admin-events';
const KEY_EVENT_CATEGORIES = 'naviex-admin-event-categories';
const KEY_EVENT_TAGS = 'naviex-admin-event-tags';

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

const FALLBACK_CATEGORIES = [
  { id: 'cat-shows', name: 'Shows e Música' },
  { id: 'cat-gastronomia', name: 'Gastronomia' },
  { id: 'cat-cultural', name: 'Cultural' },
  { id: 'cat-esportes', name: 'Esportes' },
  { id: 'cat-negocios', name: 'Negócios' },
  { id: 'cat-festival', name: 'Festival' },
  { id: 'cat-workshop', name: 'Workshop' },
  { id: 'cat-outro', name: 'Outro' },
];

const FALLBACK_TAGS = ['Gratuito', 'Pago', 'Online', 'Presencial', 'Família', 'Adulto', 'Infantil', 'Workshop', 'Gastronômico', 'Cultural', 'Musical', 'Esportivo'];

export function getEventCategories(): { id: string; name: string }[] {
  const stored = loadJson<{ id: string; name: string }[]>(KEY_EVENT_CATEGORIES, []);
  return stored.length > 0 ? stored : FALLBACK_CATEGORIES;
}

export function getEventTags(): string[] {
  const stored = loadJson<string[]>(KEY_EVENT_TAGS, []);
  return stored.length > 0 ? stored : FALLBACK_TAGS;
}

export function getEvents(): Event[] {
  return loadJson<Event[]>(KEY_EVENTS, []);
}

export function getEventById(id: string): Event | null {
  return getEvents().find((e) => e.id === id) ?? null;
}

export function getEventsByEstabelecimento(estabelecimentoId: string): Event[] {
  return getEvents().filter((e) => e.estabelecimentoId === estabelecimentoId);
}

export function addEvent(data: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>): Event {
  const now = new Date().toISOString();
  const list = getEvents();
  const next: Event = {
    ...data,
    id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    ticketTypes: data.ticketTypes ?? [],
    createdAt: now,
    updatedAt: now,
  };
  saveJson(KEY_EVENTS, [...list, next]);
  return next;
}

export function saveEvent(evt: Event): void {
  const list = getEvents();
  const idx = list.findIndex((e) => e.id === evt.id);
  if (idx < 0) throw new Error('Evento não encontrado.');
  const updated = { ...evt, updatedAt: new Date().toISOString() };
  const next = [...list];
  next[idx] = updated;
  saveJson(KEY_EVENTS, next);
}

export function removeEvent(id: string): void {
  saveJson(KEY_EVENTS, getEvents().filter((e) => e.id !== id));
}

/** Validação estilo Zod - retorna array de mensagens de erro */
export type ValidationResult = { ok: true } | { ok: false; errors: string[] };

/** Monta payload de rascunho sem validação completa (auto-save admin). */
export function mergeEventDraftDefaults(partial: Partial<Event>): Omit<Event, 'id' | 'createdAt' | 'updatedAt'> {
  const today = new Date().toISOString().slice(0, 10);
  return {
    estabelecimentoId: partial.estabelecimentoId,
    title: (partial.title ?? '').trim() || 'Rascunho',
    shortDescription: (partial.shortDescription ?? '').trim() || '—',
    description: (partial.description ?? '').trim() || '—',
    coverImage: partial.coverImage ?? '',
    categoryId: partial.categoryId ?? '',
    tags: partial.tags ?? [],
    startDate: partial.startDate || today,
    endDate: partial.endDate || today,
    startTime: partial.startTime || '09:00',
    endTime: partial.endTime || '18:00',
    timezone: partial.timezone || 'America/Sao_Paulo',
    format: partial.format ?? 'presencial',
    isFree: partial.isFree ?? true,
    maxParticipants: partial.maxParticipants,
    ticketTypes: partial.ticketTypes ?? [],
    visibility: partial.visibility ?? 'publico',
    status: 'rascunho',
    calendarioStatusPadrao: partial.calendarioStatusPadrao ?? 'ativado',
    periodoAgendaDe: partial.periodoAgendaDe ?? '',
    periodoAgendaAte: partial.periodoAgendaAte ?? '',
    periodosStatus: partial.periodosStatus ?? [],
    layoutType: partial.layoutType ?? 'normal',
    layoutExpiresAt: partial.layoutExpiresAt ?? '',
    destaqueNaviex: partial.destaqueNaviex ?? false,
    destaqueNaviexExpiresAt: partial.destaqueNaviexExpiresAt ?? '',
    destaqueSubcategoria: partial.destaqueSubcategoria ?? false,
    destaqueSubcategoriaExpiresAt: partial.destaqueSubcategoriaExpiresAt ?? '',
  };
}

/** Visível no calendário / listagens que respeitam período (não altera rascunho/publicado). */
export function isEventoCalendarioAtivo(evt: Event, data?: string): boolean {
  const hoje = data ?? new Date().toISOString().slice(0, 10);
  const periodos = evt.periodosStatus ?? [];
  for (const p of periodos) {
    if (hoje >= p.de && hoje <= p.ate) return p.tipo !== 'desativado';
  }
  return (evt.calendarioStatusPadrao ?? 'ativado') === 'ativado';
}

export function validateEvent(data: Partial<Event>): ValidationResult {
  const errors: string[] = [];
  if (!data.title || String(data.title).trim().length < 5) {
    errors.push('Título é obrigatório e deve ter no mínimo 5 caracteres.');
  }
  if (!data.shortDescription || String(data.shortDescription).trim().length === 0) {
    errors.push('Descrição curta é obrigatória.');
  }
  if (data.shortDescription && data.shortDescription.length > 160) {
    errors.push('Descrição curta deve ter no máximo 160 caracteres.');
  }
  if (!data.description || String(data.description).trim().length === 0) {
    errors.push('Descrição completa é obrigatória.');
  }
  if (!data.coverImage || String(data.coverImage).trim().length === 0) {
    errors.push('Imagem de capa é obrigatória.');
  }
  if (!data.categoryId) {
    errors.push('Categoria é obrigatória.');
  }
  if (!data.startDate) errors.push('Data de início é obrigatória.');
  if (!data.endDate) errors.push('Data de término é obrigatória.');
  if (!data.startTime) errors.push('Horário de início é obrigatório.');
  if (!data.endTime) errors.push('Horário de término é obrigatório.');
  if (!data.timezone) errors.push('Fuso horário é obrigatório.');
  if (data.startDate && data.endDate && data.startDate > data.endDate) {
    errors.push('Data de término deve ser igual ou posterior à data de início.');
  }
  if (data.isFree === false && data.ticketTypes) {
    for (let i = 0; i < data.ticketTypes.length; i++) {
      const t = data.ticketTypes[i];
      if (!t.name?.trim()) errors.push(`Tipo de ingresso ${i + 1}: nome é obrigatório.`);
      if (Number(t.price) <= 0) errors.push(`Tipo de ingresso ${i + 1}: preço deve ser maior que zero.`);
      if (Number(t.quantity) <= 0) errors.push(`Tipo de ingresso ${i + 1}: quantidade deve ser maior que zero.`);
      if (!t.currency || !['BRL', 'USD'].includes(t.currency)) {
        errors.push(`Tipo de ingresso ${i + 1}: moeda é obrigatória (BRL ou USD).`);
      }
    }
    if (!data.ticketTypes.length) {
      errors.push('Evento pago deve ter pelo menos um tipo de ingresso.');
    }
  }
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true };
}

export const TIMEZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Fortaleza',
  'America/Recife',
  'America/Belem',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Rio_Branco',
  'America/Noronha',
  'UTC',
];
